/**
 * useGroupMembershipDecisionNotifications Hook
 * Monitors decisions on user's membership requests and sends notifications
 */

import { useEffect, useRef } from 'react';
import { useSettingsStore } from '@/stores/settings-store';
import { useAuthStore } from '@/stores/auth-store';
import { useNotifications } from './useNotifications';
import { apiGet } from '@/services/api-client';
import { hasStoredExpoPushToken } from '@/utils/expo-push-token';

interface MembershipRequest {
  id: string;
  groupId: string;
  groupName: string;
  status: 'pending' | 'approved' | 'rejected';
  resolvedViaInvitation?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export function useGroupMembershipDecisionNotifications() {
  const { sendNotification, permissionStatus } = useNotifications();
  const { notifications: notificationSettings } = useSettingsStore();
  const { accessToken, user, isAuthenticated } = useAuthStore();
  const seenDecisionIdsRef = useRef<Set<string>>(new Set());
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !notificationSettings.groupMembershipDecisions || !permissionStatus.granted || !accessToken || !user?.id) {
      return;
    }

    // Skip if user has push token (mobile app) - they get backend push notifications instead
    const hasPushToken = hasStoredExpoPushToken();
    if (hasPushToken) {
      console.log('[useGroupMembershipDecisionNotifications] Skipping - user has push token, will get backend notifications');
      return;
    }

    // Poll for membership request decisions every 30 seconds
    const intervalId = setInterval(async () => {
      // Check for push token before each poll - stop polling if token exists
      const hasPushToken = hasStoredExpoPushToken();
      if (hasPushToken) {
        console.log('[useGroupMembershipDecisionNotifications] Push token detected, stopping polling');
        clearInterval(intervalId);
        return;
      }

      try {
        const res = await apiGet<{ requests: MembershipRequest[] }>('/membership-requests/my-requests');
        if (!res.success || !res.data) return;
        
        // Filter for decided requests (not via invitation, not pending)
        const decidedRequests = res.data.requests.filter(r => 
          !r.resolvedViaInvitation && r.status !== 'pending'
        );
        
        // On first run, just mark all decisions as seen (don't notify for existing decisions)
        if (!isInitializedRef.current) {
          decidedRequests.forEach(r => seenDecisionIdsRef.current.add(r.id));
          isInitializedRef.current = true;
          console.log('[useGroupMembershipDecisionNotifications] Initialized with', decidedRequests.length, 'existing decisions');
          return;
        }
        
        // Check for new decisions
        for (const request of decidedRequests) {
          if (!seenDecisionIdsRef.current.has(request.id)) {
            seenDecisionIdsRef.current.add(request.id);
            
            console.log('[useGroupMembershipDecisionNotifications] New decision detected:', request.status);
            
            // Send notification for decision
            const isApproved = request.status === 'approved';
            await sendNotification({
              title: isApproved ? 'Ansökan godkänd!' : 'Ansökan nekad',
              body: isApproved 
                ? `Du är nu medlem i ${request.groupName}` 
                : `Din ansökan till ${request.groupName} nekades`,
              data: {
                type: 'group-membership-decision',
                requestId: request.id,
                groupId: request.groupId,
                decision: request.status,
                url: isApproved ? `/app/groups/${request.groupId}` : '/app/groups/notifications',
              },
            });
          }
        }
        
        // Clean up old decision IDs from memory (keep last 100)
        if (seenDecisionIdsRef.current.size > 100) {
          const decisionIds = Array.from(seenDecisionIdsRef.current);
          seenDecisionIdsRef.current = new Set(decisionIds.slice(-100));
        }
      } catch (error) {
        console.error('[useGroupMembershipDecisionNotifications] Failed to check membership decisions:', error);
      }
    }, 30 * 1000); // 30 seconds

    // Initial load - mark all existing decisions as seen
    apiGet<{ requests: MembershipRequest[] }>('/membership-requests/my-requests')
      .then(res => {
        if (res.success && res.data) {
          const decidedRequests = res.data.requests.filter(r => !r.resolvedViaInvitation && r.status !== 'pending');
          decidedRequests.forEach(r => seenDecisionIdsRef.current.add(r.id));
          isInitializedRef.current = true;
          console.log('[useGroupMembershipDecisionNotifications] Initial load - marked', decidedRequests.length, 'existing decisions as seen');
        }
      })
      .catch(error => {
        console.error('[useGroupMembershipDecisionNotifications] Failed to initialize:', error);
      });

    return () => {
      clearInterval(intervalId);
      isInitializedRef.current = false;
    };
  }, [isAuthenticated, notificationSettings.groupMembershipDecisions, permissionStatus.granted, accessToken, user?.id, sendNotification]);
}
